/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Sep 20 2023
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */

import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Properties } from "@/frameworks/libraries/properties";
import { useChat } from "@/frameworks/core/API";
import SpaceServiceAPI from "../../API/user/useSpace/SpaceServiceAPI";

const LOGTAG = "#useChatRoomListActions#";

export default function useChatRoomListActions() {
	const chatAPI = useChat();
	const queryClient = useQueryClient();

	const { refetch: getGroupSpaceChatList } = useQuery({
		queryKey: ["getGroupSpaceChatList"],
		queryFn: async () => {
			const result: Map<string, WebServices.SpaceChat.GroupedList.Data> = new Map();
			const { data } = await chatAPI.space.groupedList.execute();
			if (data && data.myRoomChat) {
				result.set(data.myRoomChat.spaceId, {
					myRoomChat: data.myRoomChat,
					groupedSpaceChats: data.groupedSpaceChats
				});
			}
			return result;
		},
		enabled: false
	});

	const { refetch: getPublicOpenWorldChatList } = useQuery({
		queryKey: ["getPublicOpenWorldChatList"],
		queryFn: async () => {
			const result: Map<string, WebServices.Space.InqueryPublicOpenWorldInfoList.Data[number]> = new Map();
			const { data } = await SpaceServiceAPI.getInstance({}).spacePublicOpenWorld.inquiryOpenworldList.execute();
			if (!data) return result;
			data.forEach((item) => {
				result.set(item.spaceId, item);
			});
			return result;
		},
		enabled: false
	});

	const { refetch: getDMList } = useQuery({
		queryKey: ["getDMList"],
		queryFn: async () => {
			const result: Map<string, WebServices.DirectMessage.List.Data[number]> = new Map();
			const { data } = await chatAPI.dm.list.execute();
			data?.forEach((item) => {
				result.set(item.roomId, item);
			});
			return result;
		},
		enabled: false
	});

	const { refetch: getSpaceChatList } = useQuery({
		queryKey: ["getSpaceChatList"],
		queryFn: async () => {
			const result: Map<string, WebServices.SpaceChat.List.Data[number]> = new Map();
			const { data } = await chatAPI.space.list.execute();
			data?.forEach((item) => {
				item.rooms.forEach((room) => {
					if (room.spaceId) {
						result.set(room.spaceId, item);
					}
				});
			});
			return result;
		},
		enabled: false
	});

	const load = useCallback(async (type: "DM" | "Space") => {
		if (type === "DM") {
			return (await getDMList()).data;
		} else {
			// initialize 단에서 run 할때 groupSpaceChatList의 데이터는 필요하지 않아서 spaceChatList만 리턴
			const spaceChatList = (await getSpaceChatList()).data;
			await getGroupSpaceChatList();
			await getPublicOpenWorldChatList();
			return spaceChatList;
		}
	}, []);

	const addSpaceChannel = useMutation({
		mutationKey: ["getSpaceChatList"],
		mutationFn: async (spaceID: string) => {
			return {
				data: (await chatAPI.space.list.execute()).data,
				spaceID
			};
		},
		onSuccess({ data, spaceID }) {
			const createChannel = data?.filter((channel) => channel.spaceId === spaceID)[0];
			if (!createChannel) {
				throw Error("createChannel is null");
			} else {
				queryClient.setQueryData(["getSpaceChatList"], (prev: unknown) => {
					if (!prev) return prev;
					const prevList = prev as Map<string, WebServices.SpaceChat.List.Data[number]>;
					prevList.set(createChannel.spaceId, createChannel);
					return prevList;
				});
			}
		},
		onError(error) {
			console.error(error);
		}
	});

	const deleteGroupSpaceChannel = useMutation({
		mutationKey: ["getGroupSpaceChatList"],
		mutationFn: async (groupID: string) => {
			return {
				data: (await chatAPI.space.groupedList.execute()).data,
				groupID
			};
		},
		onSuccess({ data, groupID }) {
			queryClient.setQueryData(["getGroupSpaceChatList"], (prev: unknown) => {
				if (!prev) return prev;
				if (!data) return prev;

				const result: Map<string, WebServices.SpaceChat.GroupedList.Data> = new Map();
				const prevList = prev as Map<string, WebServices.SpaceChat.GroupedList.Data>;
				const myID = String(Properties.property.user?.myRoomId);
				const _data: WebServices.SpaceChat.GroupedList.Data = data;

				result.set(myID, {
					myRoomChat: prevList.get(myID)?.myRoomChat as WebServices.SpaceChat.GroupedList.Data["myRoomChat"],
					groupedSpaceChats: _data.groupedSpaceChats.filter((item) => item.groupId !== groupID)
				});
				return result;
			});
		},
		onError(error) {
			console.error(error);
		}
	});

	const deleteSpaceChannel = useMutation({
		mutationKey: ["getSpaceChatList"],
		mutationFn: async (spaceID: string) => {
			return {
				data: (await chatAPI.space.list.execute()).data,
				spaceID
			};
		},
		onSuccess({ spaceID }) {
			queryClient.setQueryData(["getSpaceChatList"], (prev: unknown) => {
				if (!prev) return prev;
				const prevList = prev as Map<string, WebServices.SpaceChat.List.Data[number]>;
				prevList.delete(spaceID);
				return prevList;
			});
		},
		onError(error) {
			console.error(error);
		}
	});

	const getSpaceChannel = useCallback((spaceID: string) => {
		return queryClient
			.getQueryData<Map<string, WebServices.SpaceChat.List.Data[number]>>(["getSpaceChatList"])
			?.get(spaceID);
	}, []);

	const getSpaceRoom = useCallback(({ spaceID, roomID }: { spaceID: string; roomID: string }) => {
		const space = queryClient
			.getQueryData<Map<string, WebServices.SpaceChat.List.Data[number]>>(["getSpaceChatList"])
			?.get(spaceID);

		if (space) {
			return space.rooms.find((v) => v.roomId === roomID);
		}
	}, []);

	const getDMRoom = useCallback((roomID: string) => {
		return queryClient
			.getQueryData<Map<string, WebServices.DirectMessage.List.Data[number]>>(["getDMList"])
			?.get(roomID);
	}, []);

	return {
		load,
		getSpaceChannel,
		getDMRoom,
		getSpaceRoom,
		addSpaceChannel,
		deleteSpaceChannel,
		getGroupSpaceChatList,
		deleteGroupSpaceChannel
	};
}
